import { FC, forwardRef, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  capitalize,
  setBonusCode,
  useApi,
} from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';

import { Button } from 'components/buttons';
import { ModalDefaultProps } from 'components/Modal';
import { ModalTemplate } from 'components/ModalTemplate';
import { Logo } from 'components/Logo';
import { ModalsContext, ModalType } from 'contexts/modal';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { bonusesData, getTodayBonusCode } from './helpers';
import { formatCurrencyForTransl } from 'helpers/currency';

import { ScBirthday } from './styled';

import birthdayBg from 'assets/images/birthday/birthdayBg.jpg';
import birthdayCharacter from 'assets/images/birthday/birthdayCharacter.png';
import { useUserCanIssueBonuses } from 'hooks/bonuses';
import { ImageComponent } from 'components/Image';
import { useMedia } from 'modules/common/hooks/useMedia';

export const BirthdayModal = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const {
      t,
      i18n: { language },
    } = useTranslation('birthday');

    const { fetchUser } = useContext(AuthContext);
    const { handleModal } = useContext(ModalsContext);
    const { isMobileS } = useMedia();

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? {
            type: 'mobile',
            mode: {
              type: 'background-custom',
              src: birthdayBg,
              position: 'left top',
            },
          }
        : {
            type: 'desktop',
            size: 'large',
            mode: {
              type: 'background-custom',
              src: birthdayBg,
              position: 'right top',
            },
          };
    }, [isMobileS]);

    const isUserCanIssueBonuses = useUserCanIssueBonuses();

    const [requestsSetBonusCode, { pending: pendingSetBonusCode }] = useApi(
      setBonusCode
    );

    const handleSubmitCode = useCallback(async () => {
      if (isUserCanIssueBonuses) {
        const { error } = await requestsSetBonusCode({
          depositBonusCode: getTodayBonusCode(),
        }).request;

        if (error != null) return;

        fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      }

      handleModal(ModalType.Deposit)();
    }, [requestsSetBonusCode, fetchUser, isUserCanIssueBonuses, handleModal]);

    const BirthdayCharacterComp: FC = useCallback(
      () => (
        <ImageComponent
          width={280}
          height={340}
          src={birthdayCharacter}
          className="character"
          alt="character"
        />
      ),
      []
    );

    const localizePrize = useCallback(
      (amount) => formatCurrencyForTransl(`{{${amount}}}`, language),
      [language]
    );

    return (
      <ModalTemplate ref={ref} onClose={onClose} settings={modalSettings}>
        <ScBirthday>
          <div className={`content ${language.toLowerCase()}`}>
            <Logo height={28} />
            <header>
              <p className="sub-title">{t('dailyMission')}</p>

              <div className="title">{t('title')}</div>
            </header>
            <div className="bday-description">
              {t('description', { prize: localizePrize(40) })}
            </div>
            <ul className="benefits">
              {bonusesData.map(({ title, description, prize, prize2 }, i) => {
                const descriptionParams = prize2
                  ? {
                      prize1: localizePrize(prize),
                      prize2: localizePrize(prize2),
                    }
                  : { prize: localizePrize(prize) };

                const descriptionTranslate = t(description, {
                  ...descriptionParams,
                });

                return (
                  <li className={`${i === 0 ? language.toLowerCase() : ''}`}>
                    <span className="title">{t(title)}</span>
                    <span className="description">
                      {capitalize(descriptionTranslate)}
                    </span>
                  </li>
                );
              })}
              {isMobileS && <BirthdayCharacterComp />}
            </ul>
            <div className={`action-btn ${language.toLowerCase()}`}>
              <Button
                green
                xLarge
                wide
                pending={pendingSetBonusCode}
                animated
                onClick={handleSubmitCode}
              >
                {t('deposit')}
              </Button>
            </div>
          </div>
          {!isMobileS && <BirthdayCharacterComp />}
        </ScBirthday>
      </ModalTemplate>
    );
  }
);
